import { CampaignType, PostClientCampaignApiBody, encodeUrlSearchParams, postClientCampaign } from '@round/api';
import { useCallback } from 'react';
import useUrlState from '../../../../../hooks/useUrlState';
import BookingSummary from '../../components/BookingSummary/BookingSummary';
import styles from './CreateCampaign.module.css';
import cn from 'classnames';
import BookingCartItemRow from '../../components/BookingCartItemRow/BookingCartItemRow';
import { ErrorMessage, Field, FieldProps, Form, Formik } from 'formik';
import { Button, FormField, Label, Select } from '@round/ui-kit';
import { GenericDropdownOption } from '../../../../../utils/utils.types';
import { showNotification } from '../../../../../utils/showNotification';
import { useNavigate } from 'react-router-dom';
import { StylesConfig } from 'react-select';
import { campaignTypeOptions } from '../../../../../utils/constants';
import { useClientBookingData } from '../../useClientBookingData';
import * as Yup from 'yup';
import { Helmet } from 'react-helmet';

type ClientCampaignFormValues = Omit<PostClientCampaignApiBody, 'bookings' | 'currency' | 'type'> & {
    brief: string;
    type: GenericDropdownOption<CampaignType>;
};

const selectStyles: StylesConfig = {
    container: (base) => ({
        ...base,
        height: '2.5rem',
        width: '14rem',
    }),
    control: (base) => ({ ...base, height: '100%' }),
    valueContainer: (base) => ({
        ...base,
        maxHeight: '100%',
    }),
};

const CreateCampaign = () => {
    const navigate = useNavigate();
    const [urlState] = useUrlState<{
        tiktok_influencer_ids: string | undefined;
        instagram_influencer_ids: string | undefined;
        currency: string | undefined;
        campaign_type: CampaignType | undefined;
    }>();

    const currencyId = Number(urlState.currency);

    const {
        bookingCartItems,
        influencerImages,
        selectedPlatforms,
        totalCost,
        totalCreators,
        influencersInitialized,
        handleSetPriceOffer,
    } = useClientBookingData(
        {
            tiktok: urlState.tiktok_influencer_ids?.split(',').map((id) => Number(id)) || [],
            instagram: urlState.instagram_influencer_ids?.split(',').map((id) => Number(id)) || [],
        },
        Number(urlState.currency),
        (urlState.campaign_type as CampaignType) || campaignTypeOptions[0].value
    );

    const isOffersExist = bookingCartItems.map((item) => item.offer).some((offer) => typeof offer === 'number');

    const isSubmissionDisabled = !bookingCartItems.length || !currencyId || !urlState.campaign_type;

    const initialValues = {
        type: campaignTypeOptions.find((option) => option.value === urlState.campaign_type) || campaignTypeOptions[0],
        brief: '',
        name: '',
    };

    const validationSchema = Yup.object({
        brief: Yup.string().min(0).required('this field cannot be empty'),
        name: Yup.string().min(0).required('this field cannot be empty'),
    });

    const handleSubmit = useCallback(
        async (values: ClientCampaignFormValues) => {
            if (!currencyId || !values.type?.value) {
                return;
            }
            const postBody: PostClientCampaignApiBody = {
                currency: currencyId,
                name: values.name,
                type: values.type.value,
                bookings: bookingCartItems.flatMap((row) => {
                    if (!row.influencerId || typeof row.cost !== 'number') {
                        return [];
                    }
                    const costToUse = row.offer ?? row.cost;
                    return [
                        {
                            brief: values.brief,
                            cost: costToUse.toFixed(2),
                            influencer_id: row.influencerId,
                            platform: row.platform.toLowerCase(),
                        },
                    ];
                }),
            };
            try {
                const response = await postClientCampaign(postBody);
                if (response.status === 400) {
                    showNotification("Couldn't create campaign", 'error');
                }
                if (response.status === 201) {
                    navigate(`/client/booking-complete/`, { replace: true });
                }
            } catch {
                showNotification("Couldn't create campaign", 'error');
            }
        },
        [bookingCartItems, currencyId, navigate]
    );

    const navigateToRoster = useCallback(() => {
        const url = `/client/roster/${encodeUrlSearchParams(urlState)}`;
        navigate(url);
    }, [urlState, navigate]);

    return (
        <>
            <Helmet>
                <title>Create Campaign</title>
            </Helmet>
            <div className={styles.wrapper}>
                <div className={styles.container}>
                    <div className={styles.header}>Booking Summary</div>
                    <div className={styles.summaryBody}>
                        <Formik
                            onSubmit={handleSubmit}
                            initialValues={initialValues}
                            validationSchema={validationSchema}
                            validateOnMount
                        >
                            {(props) => {
                                return (
                                    <Form className={styles.formContainer}>
                                        <div className={styles.form}>
                                            <FormField>
                                                <Label htmlFor="type">Booking type</Label>
                                                <Field name="type" id="type" className={styles.field}>
                                                    {(props: FieldProps<ClientCampaignFormValues['type']>) => (
                                                        <Select
                                                            isSearchable={false}
                                                            styles={selectStyles}
                                                            placeholder="Select booking type"
                                                            value={props.field.value}
                                                            onChange={(option) => {
                                                                props.form.setFieldValue(props.field.name, option);
                                                            }}
                                                            options={campaignTypeOptions}
                                                            menuPosition={'absolute'}
                                                            menuPortalTarget={document.body}
                                                            isDisabled={true}
                                                        />
                                                    )}
                                                </Field>
                                            </FormField>
                                            <FormField>
                                                <Label htmlFor="name">Campaign name</Label>
                                                <Field
                                                    name="name"
                                                    id="name"
                                                    type="text"
                                                    placeholder="Enter a name for your campaign"
                                                    className={styles.field}
                                                />
                                                <ErrorMessage name="name">
                                                    {(msg) => <span className={styles.errorMessage}>{msg}</span>}
                                                </ErrorMessage>
                                            </FormField>
                                            <FormField>
                                                <Label htmlFor="brief">Campaign details</Label>
                                                <Field
                                                    name="brief"
                                                    id="brief"
                                                    as="textarea"
                                                    resize="none"
                                                    placeholder="Enter a description of the content you would like influencers to create, as well as any important details, deliverables & deadlines."
                                                    className={styles.field}
                                                />
                                                <ErrorMessage name="brief">
                                                    {(msg) => <span className={styles.errorMessage}>{msg}</span>}
                                                </ErrorMessage>
                                            </FormField>
                                            {isOffersExist && (
                                                <div className={styles.disclaimer}>
                                                    Please note when making offers that creators may decline, as the
                                                    rate displayed is their standard base rate.
                                                </div>
                                            )}
                                        </div>
                                        <div className={styles.row}>
                                            <Button
                                                type="bordered"
                                                htmlType="button"
                                                color="black"
                                                className={styles.actionButton}
                                                onClick={navigateToRoster}
                                            >
                                                Back to Creator Selection
                                            </Button>
                                            <Button
                                                type="filled"
                                                htmlType="button"
                                                color="black"
                                                className={cn(styles.actionButton, styles.submitButton)}
                                                onClick={props.submitForm}
                                                disabled={isSubmissionDisabled || !props.isValid}
                                            >
                                                Confirm Booking
                                            </Button>
                                        </div>
                                    </Form>
                                );
                            }}
                        </Formik>
                        <div className={cn(styles.column, styles.bookingDetailsContainer)}>
                            <div className={styles.detailsTitle}>BOOKING DETAILS</div>
                            <div className={styles.detailsHeader}>
                                <BookingSummary
                                    totalCost={totalCost}
                                    totalCreators={totalCreators}
                                    platforms={selectedPlatforms}
                                    currencyId={currencyId}
                                    loading={!influencersInitialized}
                                />
                            </div>
                            <div className={styles.bookingList}>
                                {bookingCartItems.map((row) => (
                                    <BookingCartItemRow
                                        cartItem={row}
                                        influencerImage={influencerImages.find(
                                            (image) => image.influencerId === row.influencerId
                                        )}
                                        setPriceOffer={handleSetPriceOffer}
                                        currencyId={currencyId}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default CreateCampaign;
